/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { regionPhoneCode } from '@constants/regions';
import InputText from '@components/Inputs/InputText';
import Button from '@components/Forms/Button';
import Card from '@components/Card';
import { validateEmail } from '@services/users-service';
import { useForm, useAuth, useCommon, useUser, useEmailAge } from '@hooks';
import { ConfirmationModal } from '@components';

import { defaultDocType } from '@helpers/user';
import { formFieldsHaveValue } from '@helpers/validator';
import { Input } from 'fpay-ui';
import { getEnv } from '@config';
import { ADMIN } from '@constants/user';
import classes from './UserInviteForm.module.scss';
import { DocumentInput } from './DocumentInput';

const StyledInput = styled(Input)`
  height: 48px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  > div {
    margin: 32px 0 0;
    max-height: 98px;
  }
  > div:first-child {
    margin-top: 24px;
  }
  > div:last-child {
    height: auto;
  }
`;

const ButtonContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row-reverse;
  > button {
    ${theme.breakpoint('ll')} {
      margin-left: 16px;
      max-width: 97px !important;
    }
  }
`
);

const initialState = {
  country_code: `+${regionPhoneCode}`,
  doc_number: '',
  doc_type: defaultDocType,
  email: '',
  first_name: '',
  matern_last: '',
  patern_last: '',
  phone_number: '',
};

const { country } = getEnv();

const UserInviteForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const errorEmailAge = history?.location?.state?.errorEmailAge;
  const dataUser = history?.location?.state?.dataUser;
  const { setModal } = useCommon();
  const { inviteUser } = useUser();
  const { isSeller, isAlianza, isAlianzaMixto, isAllowed } = useAuth();
  const { formValues, handleInputChange, isValid, errors, setErrors } = useForm(
    errorEmailAge ? dataUser : initialState
  );
  const { isEmailAgeInvalid, setIsEmailAgeInvalid, isErrorEmailAge, showErrorSnackBar } =
    useEmailAge(!!errorEmailAge);

  useEffect(() => {
    if (errorEmailAge) {
      showErrorSnackBar();
    }
  }, []);

  const onChangeEmail = (...rest) => {
    setIsEmailAgeInvalid(false);
    handleInputChange(...rest);
  };

  const seller = isSeller() && !isAlianza() && !isAlianzaMixto();
  // TODO: delete when Peru roles are created
  const handleFlowByCountry = {
    cl: () => history.push('/configuration-users-invite-roles', formValues),
    co: () =>
      isAllowed('USER_INVITE_COLLABORATOR_CO_PE')
        ? history.push('/configuration-users-invite-roles', formValues)
        : seller
        ? setModal({
            body: (
              <ConfirmationModal
                htmlIdCancelButton="cancel-create-invitation-collaborator"
                htmlIdConfirmButton="confirm-create-invitation-collaborator"
                title={t('Onboarding.MerchantOnboarding.confirmModal.colaboratorMessage', {
                  userName:
                    `${formValues.first_name} ${formValues.patern_last} ${formValues.matern_last}`.trim(),
                })}
                handleConfirm={() => {
                  inviteUser({ ...formValues, roles: ADMIN });
                }}
              />
            ),
          })
        : history.push('/configuration-users-invite-roles', formValues),
    pe: () =>
      isAllowed('USER_INVITE_COLLABORATOR_CO_PE')
        ? history.push('/configuration-users-invite-roles', formValues)
        : seller
        ? setModal({
            body: (
              <ConfirmationModal
                htmlIdCancelButton="cancel-create-invitation-collaborator"
                htmlIdConfirmButton="confirm-create-invitation-collaborator"
                title={t('Onboarding.MerchantOnboarding.confirmModal.colaboratorMessage', {
                  userName:
                    `${formValues.first_name} ${formValues.patern_last} ${formValues.matern_last}`.trim(),
                })}
                handleConfirm={() => {
                  inviteUser({ ...formValues, roles: ADMIN });
                }}
              />
            ),
          })
        : history.push('/configuration-users-invite-roles', formValues),
  };

  const handleSubmit = () => {
    if (!isValid()) return;
    validateEmail(formValues.email)
      .then(({ message }) => {
        if (message === 'Email already was used') {
          setErrors((state) => ({ ...state, email: t('errors.email.alreadyUsed') }));
        } else {
          // eslint-disable-next-line no-unused-expressions
          handleFlowByCountry[country.toLowerCase()]?.();
        }
      })
      .catch((err) => {
        isErrorEmailAge(err);
      });
  };

  const isSubmitDisabled =
    !!Object.keys(errors).length ||
    formFieldsHaveValue(
      Object.keys(formValues)
        .filter((keyUrl) => keyUrl !== 'matern_last')
        .map((keyUrl) => formValues[keyUrl])
    );

  return (
    <div className={classes.UserInviteForm}>
      <Card
        className={classes.UserInviteForm_card}
        title="Invitar"
        subtitle={t('Onboarding.MerchantOnboarding.userInviteData.subtitle')}
      >
        <FormContainer>
          <InputText
            variant="outlined"
            onChange={handleInputChange}
            label={t('common.name')}
            id="first_name"
            value={formValues.first_name}
            error={errors.first_name}
            helperText={errors.first_name}
          />
          <InputText
            onChange={handleInputChange}
            variant="outlined"
            label={t('common.paternLastName')}
            id="patern_last"
            value={formValues.patern_last}
            error={errors.patern_last}
            helperText={errors.patern_last}
          />
          <InputText
            onChange={handleInputChange}
            variant="outlined"
            label={t('common.maternLastName')}
            id="matern_last"
            value={formValues.matern_last}
            error={errors.matern_last}
            helperText={errors.matern_last}
          />
          <DocumentInput errors={errors} formValues={formValues} handleChange={handleInputChange} />
          <InputText
            onChange={onChangeEmail}
            variant="outlined"
            label={t('common.email')}
            id="email"
            value={formValues.email}
            error={errors.email || isEmailAgeInvalid}
            helperText={errors.email}
          />
          <StyledInput
            id="phone_number"
            testId="phone-testId"
            label={t('common.phone')}
            value={formValues.phone_number}
            onChange={handleInputChange}
            error={!!errors.phone_number}
            caption={errors.phone_number}
            countryCode={regionPhoneCode}
          />
          <ButtonContainer>
            <Button
              className={classes.UserInviteForm_card_button}
              mediumSize="large"
              type="submit"
              id="confirm-collaborator-invitation-information"
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isEmailAgeInvalid}
            >
              {t('common.buttonNext')}
            </Button>
            <Button
              className={`${classes.UserInviteForm_card_button} justLarge`}
              mediumSize="large"
              variant="outlined"
              id="cancel-collaborator-invitation-information"
              onClick={() => history.push('/configuration-users')}
            >
              {t('common.buttonCancel')}
            </Button>
          </ButtonContainer>
        </FormContainer>
      </Card>
    </div>
  );
};

export default UserInviteForm;
